import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { StatusBadge, TableIdentity, type DataTableColumn } from "@h2j-data-team/ui";
import type { AnalysisDiffFinding, AnalysisDiffKind } from "@repo/shared";

import {
  formatBytes,
  formatSignedBytes,
  formatSignedCount,
  formatSignedPct,
  severityTone,
} from "@/lib/health-format";

import { ReadOnlyTable } from "./read-only-table";

// Metric keys that carry bytes/percentages/scores, so the raw numbers reach the
// translation already formatted. Anything else is interpolated as-is (counts).
const ABSOLUTE_BYTES = ["fromBytes", "toBytes", "fromWastedBytes", "toWastedBytes"];
const DELTA_BYTES = ["deltaBytes"];
const DELTA_PCT = ["deltaPct", "deltaPctOfTotal"];
const DELTA_COUNT = ["deltaScore"];

// Kinds in reading order: what got worse first, then what got better.
const KIND_ORDER: AnalysisDiffKind[] = ["regression", "improvement", "neutral"];

const KIND_TONE: Record<AnalysisDiffKind, "danger" | "success" | "neutral"> = {
  regression: "danger",
  improvement: "success",
  neutral: "neutral",
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** The title and description copy of a finding, with every metric handed over pre-formatted under its own name. */
function findingValues(finding: AnalysisDiffFinding, t: Translate) {
  // Refs preview: "FirstRefLabel, SecondRefLabel, +N more"
  const refs = (() => {
    if (finding.refs.length === 0) return "";
    if (finding.refs.length === 1) return finding.refs[0].label;
    if (finding.refs.length === 2) return `${finding.refs[0].label}, ${finding.refs[1].label}`;
    return t("findings.refsPreview", {
      first: finding.refs[0].label,
      second: finding.refs[1].label,
      rest: finding.refs.length - 2,
    });
  })();

  const values: Record<string, string | number> = { refs };
  for (const [key, value] of Object.entries(finding.metrics)) {
    if (typeof value !== "number") {
      values[key] = value;
    } else if (ABSOLUTE_BYTES.includes(key)) {
      values[key] = formatBytes(value);
    } else if (DELTA_BYTES.includes(key)) {
      values[key] = formatSignedBytes(value);
    } else if (DELTA_PCT.includes(key)) {
      values[key] = formatSignedPct(value);
    } else if (DELTA_COUNT.includes(key)) {
      values[key] = formatSignedCount(value);
    } else {
      values[key] = value;
    }
  }
  return values;
}

interface CompareFindingsListProps {
  findings: AnalysisDiffFinding[];
}

/**
 * Diff findings grouped by kind (regressions first). Same anatomy as the health
 * findings list — severity, title and detail, confidence — plus the kind.
 */
export function CompareFindingsList({ findings }: CompareFindingsListProps) {
  const { t } = useTranslation("analyses");

  const groups = useMemo(
    () =>
      KIND_ORDER.map((kind) => ({ kind, items: findings.filter((f) => f.kind === kind) })).filter(
        (g) => g.items.length > 0,
      ),
    [findings],
  );
  const rows = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  const counts = groups
    .map((g) => t(`compare.findings.count.${g.kind}`, { count: g.items.length }))
    .join(" · ");

  const columns: DataTableColumn<AnalysisDiffFinding>[] = [
    {
      id: "kind",
      header: t("compare.findings.colKind"),
      width: 140,
      cell: (finding) => (
        <StatusBadge tone={KIND_TONE[finding.kind]}>
          {t(`compare.findings.kind.${finding.kind}`)}
        </StatusBadge>
      ),
    },
    {
      id: "severity",
      header: t("findings.colSeverity"),
      width: 120,
      cell: (finding) => (
        <StatusBadge tone={severityTone(finding.severity)}>
          {t(`severity.${finding.severity}`)}
        </StatusBadge>
      ),
    },
    {
      id: "finding",
      header: t("findings.colFinding"),
      rowHeader: true,
      variant: "identity",
      cell: (finding) => {
        const values = findingValues(finding, t);
        return (
          <TableIdentity
            name={t(`findings.${finding.id}.title`, values)}
            detail={t(`findings.${finding.id}.description`, values)}
          />
        );
      },
    },
    {
      id: "confidence",
      header: t("findings.colConfidence"),
      width: 160,
      cell: (finding) => t(`confidence.${finding.confidence}`),
    },
  ];

  return (
    <section className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-[11px] font-bold tracking-[0.6px] text-faint uppercase">
          {t("compare.findings.title")}
          <span className="ml-1 font-mono text-faint">· {findings.length}</span>
        </h2>
        {counts && <span className="text-[12px] text-dim">{counts}</span>}
      </div>
      <ReadOnlyTable
        label={t("compare.findings.title")}
        columns={columns}
        rows={rows}
        getRowId={(finding) => `${finding.kind}:${finding.id}:${rows.indexOf(finding)}`}
        empty={t("compare.findings.empty")}
      />
    </section>
  );
}
